import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useApp } from "../context/AppContext";
import { apiErrorMessage } from "../lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Button } from "../components/ui/button";
import { Gauge, Eye, EyeOff, ShieldCheck, Loader2 } from "lucide-react";

export default function Login() {
  const { activeUser, login, authReady } = useApp();
  const navigate = useNavigate();
  const [mobile, setMobile] = useState("");
  const [password, setPassword] = useState("");
  const [showPwd, setShowPwd] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (authReady && activeUser) navigate("/", { replace: true });
  }, [authReady, activeUser, navigate]);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    const m = (mobile || "").replace(/\D/g, "").slice(-10);
    if (m.length !== 10) {
      setError("Enter a valid 10-digit mobile number");
      return;
    }
    if (!password) {
      setError("Password is required");
      return;
    }
    setSubmitting(true);
    try {
      await login(m, password);
      navigate("/", { replace: true });
    } catch (err) {
      const status = err?.response?.status;
      if (status === 423) setError("Account locked due to repeated failed attempts. Try again after 15 minutes or contact Admin.");
      else if (status === 401) setError("Invalid mobile number or password");
      else setError(apiErrorMessage(err, "Login failed"));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="min-h-screen w-full bg-gradient-to-br from-slate-50 via-white to-slate-100 flex items-center justify-center px-4 py-12"
      data-testid="login-page"
    >
      <div className="w-full max-w-md">
        <div className="mb-6 flex flex-col items-center gap-2">
          <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-primary text-primary-foreground shadow-lg shadow-primary/20">
            <Gauge className="h-7 w-7" strokeWidth={2.2} />
          </div>
          <div className="text-center">
            <div className="font-display text-2xl font-semibold tracking-tight text-slate-900">
              OneDesk <span className="text-primary">360</span>
            </div>
            <div className="text-[11px] font-medium uppercase tracking-[0.18em] text-slate-500">
              IVA Motors · Yamaha
            </div>
          </div>
        </div>

        <Card className="border-slate-200 shadow-xl shadow-slate-200/50">
          <CardHeader className="pb-2">
            <CardTitle className="font-display text-xl">Sign in</CardTitle>
            <p className="text-xs text-slate-500">
              Use the mobile number and password issued by your Admin.
            </p>
          </CardHeader>
          <CardContent>
            <form onSubmit={onSubmit} className="space-y-4">
              <div className="space-y-1.5">
                <Label htmlFor="login-mobile" className="text-xs">Mobile Number</Label>
                <Input
                  id="login-mobile"
                  data-testid="login-mobile-input"
                  type="tel"
                  inputMode="numeric"
                  placeholder="10-digit mobile"
                  value={mobile}
                  onChange={(e) => setMobile(e.target.value.replace(/\D/g, "").slice(0, 10))}
                  autoFocus
                  autoComplete="username"
                  maxLength={10}
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="login-password" className="text-xs">Password</Label>
                <div className="relative">
                  <Input
                    id="login-password"
                    data-testid="login-password-input"
                    type={showPwd ? "text" : "password"}
                    placeholder="Enter password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    autoComplete="current-password"
                    className="pr-10"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPwd((v) => !v)}
                    className="absolute right-2 top-1/2 -translate-y-1/2 rounded p-1 text-slate-400 hover:text-slate-600"
                    data-testid="login-toggle-pwd"
                    tabIndex={-1}
                  >
                    {showPwd ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>

              {error && (
                <div
                  className="rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-xs font-medium text-rose-700"
                  data-testid="login-error"
                >
                  {error}
                </div>
              )}

              <Button
                type="submit"
                className="w-full gap-2"
                disabled={submitting}
                data-testid="login-submit-btn"
              >
                {submitting ? (
                  <><Loader2 className="h-4 w-4 animate-spin" /> Signing in...</>
                ) : (
                  <><ShieldCheck className="h-4 w-4" /> Sign in</>
                )}
              </Button>

              <p className="pt-1 text-center text-[11px] text-slate-500">
                Forgot password? Contact your Admin to reset it.
              </p>
            </form>
          </CardContent>
        </Card>

        <p className="mt-4 text-center text-[10px] uppercase tracking-[0.2em] text-slate-400">
          Sessions expire after 12 hours
        </p>
      </div>
    </div>
  );
}
